import React, { useState, useEffect, useRef } from 'react';
import { Input, DatePicker } from 'antd';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import FormItem from 'src/components/form-item';
import SimpleSelect from 'src/components/simple-select';
import CITTreeSelect from 'src/components/tree-select';
import Button, { Type as ButtonType } from 'src/components/button';
import { CloseOutlined } from '@ant-design/icons';
import './style.scss';

const { Search } = Input;
const { RangePicker } = DatePicker;

/**
 * Filters component for Inactive Profiles report.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onApiFilter - Callback invoked when filters change.
 * @param {Object} props.filterOptions - Available options for each filter dropdown.
 * @returns {JSX.Element} The InactivationFilters component.
 */
function InactivationFilters({ onApiFilter, filterOptions }) {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [pdm, setPdm] = useState([]);
  const [allianceAndGu, setAllianceAndGu] = useState([]);
  const [profileStatus, setProfileStatus] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [clearFilter, setClearFilter] = useState(false);

  const stateRef = useRef({ login, pdm, allianceAndGu, profileStatus, dateRange });

  const statusOptions = [
    { value: 'active', label: t('active') },
    { value: 'inactive', label: t('inactive') },
  ];

  useEffect(() => {
    stateRef.current = { login, pdm, allianceAndGu, profileStatus, dateRange };
  }, [login, pdm, allianceAndGu, profileStatus, dateRange]);

  const buildFilters = (overrides = {}) => {
    const { login: l, pdm: p, allianceAndGu: g, profileStatus: s, dateRange: d } = {
      ...stateRef.current,
      ...overrides,
    };
    const f = {};
    if (l) f.login = l;
    if (p.length > 0) f.pdm = p;
    if (g.length > 0) f.allianceAndGu = g;
    if (s.length > 0) f.profileStatus = s;
    if (d && d[0] && d[1]) {
      f.inactivationStartDate = d[0].format('YYYY-MM-DD');
      f.inactivationEndDate = d[1].format('YYYY-MM-DD');
    }
    return f;
  };

  const handleDropdownClose = (open) => {
    if (!open) onApiFilter(buildFilters());
  };

  const handleDeselect = (filterKey, currentValues) => (removedValue) => {
    const newVals = currentValues.filter(v => v !== removedValue);
    onApiFilter(buildFilters({ [filterKey]: newVals }));
  };

  const handleSelect = (filterKey, currentValues) => (selectedValue) => {
    onApiFilter(buildFilters({ [filterKey]: [...currentValues, selectedValue] }));
  };

  const handleClear = (filterKey) => () => {
    onApiFilter(buildFilters({ [filterKey]: [] }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    onApiFilter(buildFilters({ dateRange: dates }));
  };

  useEffect(() => {
    if (clearFilter) {
      setLogin('');
      setPdm([]);
      setAllianceAndGu([]);
      setProfileStatus([]);
      setDateRange(null);
      onApiFilter({});
    }
    setClearFilter(false);
  }, [clearFilter]);

  return (
    <div className='reports-filter__container'>
      <FormItem label='Login' id='reports-inactivation-login'>
        <Search
          id='reports-inactivation-login'
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          onSearch={(val) => onApiFilter(buildFilters({ login: val }))}
          placeholder={t('searchByLogin')}
        />
      </FormItem>
      <FormItem label='PDM' id='reports-inactivation-pdm'>
        <SimpleSelect
          options={filterOptions.pdm}
          onChange={setPdm}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onSelect={handleSelect('pdm', pdm)}
          onDeselect={handleDeselect('pdm', pdm)}
          onClear={handleClear('pdm')}
        />
      </FormItem>
      <FormItem label='GU' id='reports-inactivation-gu'>
        <CITTreeSelect
          options={filterOptions.alliance}
          onChange={setAllianceAndGu}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onDeselect={handleDeselect('allianceAndGu', allianceAndGu)}
          onClear={handleClear('allianceAndGu')}
        />
      </FormItem>
      <FormItem label={t('statusProfile')} id='reports-inactivation-status'>
        <SimpleSelect
          options={statusOptions}
          onChange={setProfileStatus}
          clear={clearFilter}
          onDropdownVisibleChange={handleDropdownClose}
          onSelect={handleSelect('profileStatus', profileStatus)}
          onDeselect={handleDeselect('profileStatus', profileStatus)}
          onClear={handleClear('profileStatus')}
          mode='multiple'
          value={profileStatus}
        />
      </FormItem>
      <FormItem label={t('reports.inactivationPeriod')} id='reports-inactivation-period'>
        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format='DD/MM/YYYY'
          placeholder={[t('startDate'), t('endDate')]}
        />
      </FormItem>
      <FormItem>
        <Button type={ButtonType.Clear} className='btn-clear-filters' onClick={() => setClearFilter(true)}>
          <span className='btn-clear-content'>
            <CloseOutlined className='icon-x' />
            <span className='btn-clear-text'>{t('clearFilters')}</span>
          </span>
        </Button>
      </FormItem>
    </div>
  );
}

InactivationFilters.defaultProps = {
  onApiFilter: noop,
  filterOptions: {},
};

export default InactivationFilters;
